/*
 * <home-menu-bar>
 * Home navigation bar.
 * - Desktop: shows prev/next arrows when the row overflows, and supports
 *   click-and-drag (mouse only) to scroll the row. Overflow is tracked via a
 *   ResizeObserver + the nav scroll event, so it stays correct on resize and
 *   on Shopify section reloads.
 * - Mobile: scrolls the active item into view once on connect (no arrows).
 * - connectedCallback makes it theme-editor safe, multi-instance safe, and
 *   adds no globals.
 */
class HomeMenuBar extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('.home-menu-bar__nav');
    if (!this.nav) return;

    this.prevButton = this.querySelector('.home-menu-bar__arrow--prev');
    this.nextButton = this.querySelector('.home-menu-bar__arrow--next');

    this.onScroll = this.updateArrows.bind(this);
    this.onResize = this.checkOverflow.bind(this);

    this.nav.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollByStep(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollByStep(1));
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.nav);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    this.bindDrag();
    this.checkOverflow();
    this.scrollActiveIntoView();
  }

  disconnectedCallback() {
    if (this.nav) {
      this.nav.removeEventListener('scroll', this.onScroll);
      this.unbindDrag();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.onResize);
    }
  }

  // Toggle the scrollable state (drives arrow visibility via CSS) and refresh
  // the enabled/disabled state of each arrow.
  checkOverflow() {
    const isScrollable = this.nav.scrollWidth - this.nav.clientWidth > 1;
    this.classList.toggle('is-scrollable', isScrollable);
    this.updateArrows();
  }

  updateArrows() {
    if (!this.classList.contains('is-scrollable')) return;
    const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;
    const left = this.nav.scrollLeft;
    if (this.prevButton) this.prevButton.disabled = left <= 1;
    if (this.nextButton) this.nextButton.disabled = left >= maxScroll - 1;
  }

  // Scroll by ~80% of the visible width in the given direction (-1 / +1).
  scrollByStep(direction) {
    const amount = Math.max(this.nav.clientWidth * 0.8, 120);
    this.nav.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  // Desktop click-and-drag to scroll. Mouse only — touch/pen keep native
  // scrolling so mobile swipe is untouched. A drag past a small threshold
  // suppresses the following click so links don't navigate after a drag.
  bindDrag() {
    this.isDragging = false;
    this.dragMoved = false;

    this.onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('.home-menu-bar__arrow')) return;
      if (this.nav.scrollWidth - this.nav.clientWidth <= 1) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.nav.scrollLeft;
      this.classList.add('is-dragging');
      try {
        this.nav.setPointerCapture(event.pointerId);
      } catch (e) {}
    };

    this.onPointerMove = (event) => {
      if (!this.isDragging) return;
      const dx = event.clientX - this.dragStartX;
      if (Math.abs(dx) > 3) this.dragMoved = true;
      this.nav.scrollLeft = this.dragStartScroll - dx;
    };

    this.onPointerUp = (event) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.classList.remove('is-dragging');
      try {
        this.nav.releasePointerCapture(event.pointerId);
      } catch (e) {}
    };

    // Block the click that fires right after a real drag (capture phase).
    this.onClickCapture = (event) => {
      if (!this.dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    };

    // Prevent the browser's native link-drag ghost while dragging.
    this.onDragStart = (event) => {
      if (this.isDragging) event.preventDefault();
    };

    this.nav.addEventListener('pointerdown', this.onPointerDown);
    this.nav.addEventListener('pointermove', this.onPointerMove);
    this.nav.addEventListener('pointerup', this.onPointerUp);
    this.nav.addEventListener('pointercancel', this.onPointerUp);
    this.nav.addEventListener('click', this.onClickCapture, true);
    this.nav.addEventListener('dragstart', this.onDragStart);
  }

  unbindDrag() {
    this.nav.removeEventListener('pointerdown', this.onPointerDown);
    this.nav.removeEventListener('pointermove', this.onPointerMove);
    this.nav.removeEventListener('pointerup', this.onPointerUp);
    this.nav.removeEventListener('pointercancel', this.onPointerUp);
    this.nav.removeEventListener('click', this.onClickCapture, true);
    this.nav.removeEventListener('dragstart', this.onDragStart);
  }

  // Mobile only: center the active item once on connect.
  scrollActiveIntoView() {
    if (this.dataset.scrollToActive !== 'true') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const activeItem = this.querySelector('.home-menu-bar__item.is-active');
    if (!activeItem) return;

    window.requestAnimationFrame(() => {
      const navRect = this.nav.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const offset =
        itemRect.left - navRect.left - navRect.width / 2 + itemRect.width / 2;
      this.nav.scrollTo({ left: this.nav.scrollLeft + offset, behavior: 'smooth' });
    });
  }
}

if (!customElements.get('home-menu-bar')) {
  customElements.define('home-menu-bar', HomeMenuBar);
}
